import { useRef, type CSSProperties } from "react";
import { Button, Chip, Panel, StatGrid } from "@legends/ui";
import ConsumerWatch from "./ConsumerWatch";
import { AsyncState, formatDateTime, formatKind, formatPrice, isProrated, monoLabelStyle, proratedNote, type LoadStatus } from "./sections";
import type { CaveEvent, Legend, LegendCavecast, LiveBroadcast, TicketType } from "./api";

// The Legend Cave profile (ADR 0020) — the designer's rich storefront: a framed
// hero with the season-pass CTA, career stats, cavecast schedule, about, and
// charities. Commerce (Buy / Watch) stays owned by CavePublicScreen; this is the
// presentation, driven entirely by real legend + cave data.

export interface LegendCaveProps {
  legend: Legend;
  tickets: TicketType[];
  live: LiveBroadcast[];
  /** The cave's REAL upcoming events (ADR 0049) — the actual buy/watch targets,
   *  distinct from the profile's seeded cavecast flavor. Empty for a roster-only
   *  legend with no cave. */
  events?: CaveEvent[];
  /** The load state of `events` so the schedule can show loading / empty / error
   *  distinctly (ADR 0049). Defaults to "ready" (roster-only profile view). */
  eventsStatus?: LoadStatus;
  /** Retry the events read after a failure. */
  onRetryEvents?: () => void;
  owned: Set<string>;
  acquired: Set<string>;
  /** The fan holds VIP all-access (LSC-549): tickets reflect access, not a Buy nag. */
  vip?: boolean;
  signedIn: boolean | null;
  busyId: string | null;
  watching: string | null;
  onBuy: (t: TicketType) => void;
  onWatch: (broadcastId: string) => void;
  onStopWatch: () => void;
  onSignIn?: () => void;
}

const heroGridWithPass: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "minmax(160px, 220px) 1fr minmax(200px, 260px)",
  gap: 20,
  alignItems: "stretch",
};
const heroGridNoPass: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "minmax(160px, 220px) 1fr",
  gap: 20,
  alignItems: "stretch",
};

/** A framed "memorabilia" headshot — initials on a dark mat with a brass
 *  hairline (the handoff's fallback treatment for trading-card assets). */
function Headshot({ initials }: { initials: string }) {
  return (
    <div
      aria-hidden
      style={{
        aspectRatio: "3 / 4",
        borderRadius: "var(--radius-md)",
        border: "1px solid var(--border-mute)",
        background: "linear-gradient(160deg, var(--stone-600), var(--stone-800))",
        boxShadow: "inset 0 0 0 4px rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 56,
          fontWeight: 600,
          color: "var(--gold)",
          opacity: 0.6,
          letterSpacing: "0.04em",
        }}
      >
        {initials}
      </span>
    </div>
  );
}

/** Choose the pass to headline the hero CTA: the priciest season / all-access
 *  ticket, else the priciest ticket overall. */
function seasonPass(tickets: TicketType[]): TicketType | null {
  if (tickets.length === 0) return null;
  const passes = tickets.filter((t) => t.kind === "season" || t.kind === "all_access");
  const pool = passes.length > 0 ? passes : tickets;
  return pool.reduce((a, b) => (b.priceCents > a.priceCents ? b : a));
}

export default function LegendCave(props: LegendCaveProps) {
  const {
    legend,
    tickets,
    live,
    events = [],
    eventsStatus = "ready",
    onRetryEvents,
    owned,
    acquired,
    vip = false,
    signedIn,
    busyId,
    watching,
    onBuy,
    onWatch,
    onStopWatch,
    onSignIn,
  } = props;
  const p = legend.profile!;
  const ticketsRef = useRef<HTMLDivElement | null>(null);
  const pass = seasonPass(tickets);
  const scrollToTickets = () => ticketsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {/* HERO */}
      <Panel tier="raised" aria-label={`${legend.name} — Legend Cave`} style={{ padding: 0, overflow: "hidden" }}>
        <div style={pass ? heroGridWithPass : heroGridNoPass} className="lc-hero">
          <div style={{ padding: "18px 0 18px 18px" }}>
            <Headshot initials={legend.initials} />
          </div>
          <div style={{ padding: "22px 4px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <h1
              style={{
                margin: 0,
                fontFamily: "var(--font-display)",
                fontSize: "clamp(30px, 4vw, 44px)",
                fontWeight: 700,
                letterSpacing: "0.01em",
                color: "var(--text-hi)",
                lineHeight: 1.02,
                textTransform: "uppercase",
              }}
            >
              {legend.name}
            </h1>
            <p style={{ margin: "8px 0 0", fontSize: "var(--fs-body)", color: "var(--text-prose)" }}>{p.sub}</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 14 }}>
              {p.tags.map((t) => (
                <span
                  key={t}
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: "var(--fs-2xs)",
                    color: "var(--gold-2)",
                    border: "1px solid var(--border-mute)",
                    borderRadius: "var(--radius-pill)",
                    padding: "3px 11px",
                    background: "var(--accent-tint)",
                  }}
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
          {/* Season-pass CTA — only when the cave actually sells passes */}
          {pass && (
            <div
              style={{
                borderLeft: "1px solid var(--hairline)",
                padding: "22px 20px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                gap: 4,
              }}
            >
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 46,
                  fontWeight: 700,
                  color: "var(--gold)",
                  lineHeight: 1,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {isProrated(pass) && (
                  <s style={{ opacity: 0.5, marginRight: 10, fontWeight: 400 }}>{formatPrice(pass.priceCents)}</s>
                )}
                {formatPrice(pass.proratedPriceCents)}
              </div>
              <div style={{ ...monoLabelStyle, marginBottom: 12 }}>
                {proratedNote(pass) || "Full Season Pass"}
              </div>
              {vip ? (
                <div style={{ ...monoLabelStyle, color: "var(--success-hi)" }} aria-label="VIP access">VIP access ✓</div>
              ) : (
                <Button
                  variant="accent"
                  aria-label={signedIn === false ? "Sign in to purchase" : `Purchase ${pass.name}`}
                  disabled={busyId === pass.id}
                  onClick={() => (signedIn === false ? onSignIn?.() : onBuy(pass))}
                >
                  {signedIn === false ? "Sign in to buy" : owned.has(pass.id) ? "Owned ✓" : "Purchase Season Ticket"}
                </Button>
              )}
            </div>
          )}
        </div>
      </Panel>

      {/* LIVE NOW — real broadcasts, entitlement-gated */}
      {watching ? (
        <Panel tier="base" title="Live now" aria-label="Watch live program">
          <ConsumerWatch broadcastId={watching} onClose={onStopWatch} onGetTicket={scrollToTickets} legend={legend} />
        </Panel>
      ) : live.length > 0 ? (
        <Panel tier="base" title="Live now">
          <div style={{ display: "grid", gap: 8 }}>
            {live.map((b) => (
              <div key={b.broadcastId} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span aria-hidden style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--alert-2)", boxShadow: "var(--glow-alert)" }} />
                <span style={{ fontSize: "var(--fs-body)", color: "var(--text-hi)" }}>{b.title}</span>
                <div style={{ marginLeft: "auto" }}>
                  {signedIn === false ? (
                    <Button size="sm" variant="accent" aria-label={`Sign in to watch ${b.title}`} onClick={() => onSignIn?.()}>
                      Sign in to watch
                    </Button>
                  ) : (
                    <Button size="sm" variant="accent" aria-label={`Watch ${b.title}`} onClick={() => onWatch(b.broadcastId)}>
                      Watch
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Panel>
      ) : null}

      {/* UPCOMING CAVECASTS — the cave's REAL scheduled events (ADR 0049): the
          actual buy/watch targets, sourced from /public/cave/:id/events, NOT the
          profile's seeded cavecast flavor. Loading / empty / error are distinct so
          a failed fetch never looks like an empty schedule. */}
      <Panel tier="base" title="Upcoming CaveCasts">
        {eventsStatus === "error" ? (
          <AsyncState
            status="error"
            errorText="Couldn't load the schedule."
            onRetry={onRetryEvents}
            compact
          />
        ) : eventsStatus === "loading" ? (
          <AsyncState status="loading" loadingText="Loading the schedule…" compact />
        ) : events.length > 0 ? (
          <div style={{ display: "grid", gap: 4 }}>
            {events.map((e) => (
              <div
                key={e.id}
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 10,
                  padding: "9px 0",
                  borderTop: "1px solid var(--border-faint)",
                }}
              >
                <span style={{ ...monoLabelStyle, color: "var(--gold)", minWidth: 172 }}>{formatDateTime(e.startsAt)}</span>
                <span style={{ fontSize: "var(--fs-body)", color: "var(--text-hi)" }}>{e.title}</span>
                <span style={{ ...monoLabelStyle, marginLeft: "auto", color: "var(--text-dim)" }}>{e.durationMinutes} MIN</span>
              </div>
            ))}
          </div>
        ) : (
          <AsyncState status="empty" emptyText="No upcoming CaveCasts scheduled." compact />
        )}
      </Panel>

      {/* CAREER STATS */}
      {p.stats.length > 0 && (
        <Panel tier="base" title="Career Stats">
          <StatGrid items={p.stats.map((s) => ({ value: s.val, label: s.label }))} columns={Math.min(6, p.stats.length)} />
        </Panel>
      )}

      {/* CAVECAST HIGHLIGHTS (flavor) + ABOUT — the profile's seeded cavecasts are
          descriptive highlights, deliberately NOT presented as actionable/dated
          events (the real schedule lives in Upcoming CaveCasts above — ADR 0049). */}
      <div
        style={{ display: "grid", gridTemplateColumns: p.cavecasts.length > 0 ? "1fr 1fr" : "1fr", gap: 16 }}
        className="lc-cols"
      >
        {p.cavecasts.length > 0 && (
          <Panel tier="quiet" title="Cavecast Highlights">
            <HighlightList rows={p.cavecasts} />
          </Panel>
        )}
        <Panel tier="quiet" title={p.aboutTitle || "About"}>
          <p style={{ margin: 0, fontSize: "var(--fs-body-2)", color: "var(--text-prose)", lineHeight: "var(--lh-prose)" }}>
            {p.bio}
          </p>
          {p.roles && (
            <p style={{ margin: "12px 0 0", ...monoLabelStyle, color: "var(--gold)" }}>{p.roles}</p>
          )}
        </Panel>
      </div>

      {/* TICKETS — the real commerce (only when the cave sells any) */}
      {tickets.length > 0 && (
      <div ref={ticketsRef}>
        <Panel tier="base" title="Tickets">
          <div style={{ display: "grid", gap: 4 }}>
            {tickets.map((t) => {
              const held = owned.has(t.id) || acquired.has(t.id);
              const isOwned = vip || held;
              return (
                <div
                  key={t.id}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderTop: "1px solid var(--border-faint)" }}
                >
                  <span style={{ fontSize: "var(--fs-body)", color: "var(--text-hi)" }}>{t.name}</span>
                  <Chip>{formatKind(t.kind)}</Chip>
                  <span
                    style={{
                      marginLeft: "auto",
                      fontFamily: "var(--font-display)",
                      fontSize: "var(--fs-lg)",
                      color: "var(--gold)",
                      fontVariantNumeric: "tabular-nums",
                    }}
                    title={proratedNote(t) || undefined}
                  >
                    {isProrated(t) && (
                      <s style={{ opacity: 0.5, marginRight: 6, fontWeight: 400 }}>{formatPrice(t.priceCents)}</s>
                    )}
                    {formatPrice(t.proratedPriceCents)}
                  </span>
                  {isOwned ? (
                    <span style={{ ...monoLabelStyle, color: "var(--success-hi)" }}>{vip && !held ? "VIP access ✓" : "Owned ✓"}</span>
                  ) : (
                    <Button
                      size="sm"
                      variant="accent"
                      aria-label={signedIn === false ? `Sign in to buy ${t.name}` : `Buy ${t.name}`}
                      disabled={busyId === t.id}
                      onClick={() => (signedIn === false ? onSignIn?.() : onBuy(t))}
                    >
                      {signedIn === false ? "Sign in to buy" : "Buy"}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </Panel>
      </div>
      )}

      {/* CHARITIES & CAUSES */}
      {p.charities.length > 0 && (
        <Panel tier="base" title={`${legend.name.split(" ")[0]}'s Charities & Causes`}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            {p.charities.map((c) => (
              <div
                key={c.name}
                style={{
                  padding: "14px 16px",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--border-faint)",
                  background: "var(--surface-quiet)",
                }}
              >
                <div style={{ fontFamily: "var(--font-display)", fontSize: "var(--fs-lg)", color: "var(--text-hi)", marginBottom: 5 }}>
                  {c.name}
                </div>
                <p style={{ margin: 0, fontSize: "var(--fs-body-2)", color: "var(--text-prose)", lineHeight: "var(--lh-normal)" }}>
                  {c.desc}
                </p>
              </div>
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
}

/** The profile's seeded cavecasts rendered as descriptive highlights (ADR 0049):
 *  a caption + title, deliberately WITHOUT a live/on-demand action badge so it
 *  reads as editorial flavor, not a bookable event. The real, dated schedule
 *  lives in the Upcoming CaveCasts panel driven by the cave's events. */
function HighlightList({ rows }: { rows: LegendCavecast[] }) {
  return (
    <div style={{ display: "grid", gap: 12 }}>
      {rows.map((c, i) => (
        <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          <span style={{ ...monoLabelStyle, color: "var(--gold)", minWidth: 58, flexShrink: 0 }}>{c.date}</span>
          <span style={{ fontSize: "var(--fs-body-2)", color: "var(--text)", flex: 1 }}>{c.title}</span>
        </div>
      ))}
    </div>
  );
}
